import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/comics';
import { useState } from 'react';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '基德漫画网',
    url: 'https://jidemanhua.com',
    description: '基德漫画网汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅，优质韩漫作品持续上新，满足阅读需求。',
    email: 'contact@jidemanhua.com',
    foundingDate: '2022',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@jidemanhua.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const pageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 基德漫画网',
    description: '基德漫画网品牌介绍，汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅。',
    url: 'https://jidemanhua.com/about',
    about: {
      '@type': 'Organization',
      name: '基德漫画网',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '基德漫画网的韩国漫画是否有删减？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '基德漫画网坚持提供无删减版韩国漫画，所有内容保持原版完整性，带给读者最原汁原味的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '基德漫画网是否需要付费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '基德漫画网是免费韩漫大全平台，所有漫画均可免费在线阅读，无需付费订阅。',
        },
      },
      {
        '@type': 'Question',
        name: '基德漫画网的更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '基德漫画网每日持续更新，确保读者能够第一时间阅读到最新的韩国漫画章节。优质韩漫作品持续上新。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在基德漫画网阅读漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '基德漫画网采用下拉式阅读方式，读者只需在分类页面选择喜欢的漫画，点击即可进入详情页查看章节列表，选择章节开始阅读，操作简单流畅。',
        },
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9pcltaqs38sj6jc7lsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络连接失败，请检查网络后重试。');
    }
  };

  const features = [
    { icon: 'ri-shield-check-line', title: '无删减原版', desc: '所有韩国漫画均为无删减版本，保持原版完整性，提供最原汁原味的阅读体验。' },
    { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '采用下拉式流畅阅读模式，无需翻页，一气呵成的漫画阅读体验。' },
    { icon: 'ri-book-open-line', title: '海量作品', desc: '汇聚各类韩国漫画，涵盖恋爱、动作、奇幻、剧情等多种分类，满足不同阅读需求。' },
    { icon: 'ri-flashlight-line', title: '每日更新', desc: '优质韩漫作品持续上新，确保读者能够第一时间阅读到最新更新的内容。' },
    { icon: 'ri-money-cny-circle-line', title: '完全免费', desc: '免费韩漫大全平台，所有漫画均可免费在线阅读，无需付费订阅。' },
    { icon: 'ri-eye-line', title: '高清画质', desc: '所有漫画提供高清画质，确保每一处细节清晰呈现，享受最佳视觉体验。' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(pageJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>

      <div className="pt-20 pb-8 md:pb-10 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-4">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              关于基德漫画网
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              汇聚无删减韩国漫画，下拉式免费韩漫大全在线阅读流畅
            </p>
          </div>

          <section className="mt-8">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              品牌介绍
            </h2>
            <div className="prose prose-sm max-w-none text-foreground-700">
              <p className="leading-relaxed">
                <strong>基德漫画网</strong>是专注于<strong>韩国漫画</strong>的在线阅读平台，致力于为广大韩漫爱好者提供优质的<strong>无删减漫画</strong>阅读服务。
                我们采用创新的<strong>下拉式韩漫</strong>阅读模式，让读者能够流畅地享受每一部作品，无需繁琐的翻页操作。
              </p>
              <p className="leading-relaxed mt-3">
                作为<strong>免费韩漫大全</strong>平台，基德漫画网汇聚了涵盖恋爱、动作、奇幻、剧情、悬疑、搞笑、惊悚、日常等多种分类的韩国漫画作品。
                我们与多家韩国漫画版权方保持合作，确保<strong>优质韩漫作品持续上新</strong>，让读者总能找到自己喜爱的内容。
              </p>
              <p className="leading-relaxed mt-3">
                自成立以来，基德漫画网始终坚持"内容为王、用户至上"的理念，不断优化阅读体验，提升内容质量。
                我们的目标是成为国内最受信赖的韩国漫画阅读平台，满足每一位韩漫爱好者的阅读需求。
              </p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {features.map((feat) => (
                <div key={feat.title} className="flex items-start gap-3 p-4 rounded-lg bg-background-100">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${feat.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">{feat.title}</h3>
                    <p className="text-xs text-foreground-600 mt-1 leading-relaxed">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-100">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-xs text-accent-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <p className="text-xs text-foreground-400 mt-2">{t.date}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-10 mb-6">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-sm text-primary-500"></i>
                    </div>
                    <a href="mailto:contact@jidemanhua.com" className="text-sm text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer">
                      contact@jidemanhua.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-global-line text-sm text-primary-500"></i>
                    </div>
                    <span className="text-sm text-foreground-700">https://jidemanhua.com</span>
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-lg bg-background-100">
                <h3 className="text-sm font-semibold text-foreground-950 mb-3">给我们留言</h3>
                {formStatus === 'success' ? (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto">
                      <i className="ri-check-line text-xl text-primary-500"></i>
                    </div>
                    <p className="mt-3 text-sm font-medium text-foreground-950">感谢您的留言！</p>
                    <p className="text-xs text-foreground-500 mt-1">我们会尽快回复您。</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="sp-hp"
                    />
                    <div>
                      <label htmlFor="about-name" className="text-xs font-medium text-foreground-700 block mb-1">
                        姓名
                      </label>
                      <input
                        id="about-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="text-xs font-medium text-foreground-700 block mb-1">
                        邮箱
                      </label>
                      <input
                        id="about-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-message" className="text-xs font-medium text-foreground-700 block mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>
                    {formStatus === 'error' && formError && (
                      <p className="text-xs text-red-500">{formError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="inline-flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i>
                          发送中...
                        </span>
                      ) : (
                        '发送留言'
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '基德漫画网的韩国漫画是否有删减？',
                  a: '基德漫画网坚持提供无删减版韩国漫画，所有内容保持原版完整性，带给读者最原汁原味的阅读体验。',
                },
                {
                  q: '基德漫画网是否需要付费？',
                  a: '基德漫画网是免费韩漫大全平台，所有漫画均可免费在线阅读，无需付费订阅。',
                },
                {
                  q: '基德漫画网的更新频率如何？',
                  a: '基德漫画网每日持续更新，确保读者能够第一时间阅读到最新的韩国漫画章节。优质韩漫作品持续上新。',
                },
                {
                  q: '如何在基德漫画网阅读漫画？',
                  a: '基德漫画网采用下拉式阅读方式，读者只需在分类页面选择喜欢的漫画，点击即可进入详情页查看章节列表，选择章节开始阅读，操作简单流畅。',
                },
              ].map((faq) => (
                <details key={faq.q} className="group bg-background-100 rounded-lg">
                  <summary className="flex items-center justify-between cursor-pointer p-4 text-sm font-medium text-foreground-950 list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-4 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
}